import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Square, AlertTriangle, Phone, MapPin, ClipboardList, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { useFamily } from "../context/FamilyContext";
import { api, errMsg, fmtDate, fmtTime, fmtClock } from "../lib/api";
import { PageTitle, Emergency911 } from "../components/Bits";
import { useAutosave } from "../lib/useAutosave";

const getLoc = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    const t = setTimeout(() => resolve({}), 7000);
    navigator.geolocation.getCurrentPosition(
      (p) => { clearTimeout(t); resolve({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => { clearTimeout(t); resolve({}); },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 600000 },
    );
  });

function NotRight({ open, setOpen, briefId }) {
  const { fid } = useFamily();
  const [b, setB] = useState(undefined);
  useEffect(() => {
    if (!briefId) return setB(null);
    api.get(`/families/${fid}/briefs/${briefId}`).then((r) => setB(r.data)).catch(() => setB(null));
    return undefined;
  }, [briefId, fid]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" data-testid="not-right-sheet" className="rounded-t-3xl bg-hf-cream pb-10">
        <SheetHeader>
          <SheetTitle className="text-left font-heading text-2xl">Take a breath. Here's who to call.</SheetTitle>
        </SheetHeader>
        <a href="tel:911" data-testid="not-right-call-911" className="hf-btn mt-6 w-full bg-hf-brick text-white">
          <Phone className="h-5 w-5" /> Emergency? Call 911
        </a>
        <div className="mt-6 space-y-2">
          <p className="text-sm font-semibold uppercase tracking-wider text-hf-muted">From the family's brief</p>
          {b === undefined ? (
            <p className="text-lg text-hf-muted">Loading contacts...</p>
          ) : b?.output?.who_to_call?.length ? (
            b.output.who_to_call.map((w, i) => <p key={i} data-testid={`not-right-contact-${i}`} className="rounded-xl bg-white p-4 text-lg">{w}</p>)
          ) : (
            <p className="text-lg text-hf-muted">No brief shared yet. Call the family caregiver.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Elapsed({ since }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const m = Math.max(0, Math.floor((Date.now() - new Date(since)) / 60000));
  return <span data-testid="visit-elapsed">{Math.floor(m / 60)}h {m % 60}m</span>;
}

export default function Visit() {
  const { fid, base, data, refresh, roles } = useFamily();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [note, setNote, clearNote] = useAutosave(`handoff_visit_note_${fid}`, "");
  const visit = data.visits.find((v) => v.status === "in_progress") || data.visits.find((v) => v.status === "upcoming");
  const brief = data.briefs.find((b) => b.status === "approved" && (!visit || b.visit_id === visit.id)) || data.briefs.find((b) => b.status === "approved");

  if (!roles.includes("helper")) return <p className="text-lg">Visit mode is for the helper.</p>;
  if (!visit) return (
    <div className="space-y-4">
      <PageTitle eyebrow="Visit mode" title="No visit planned yet." sub="When the family books a break, it shows up here." />
    </div>
  );

  const act = async (kind) => {
    setBusy(true);
    try {
      const loc = await getLoc();
      await api.post(`/families/${fid}/visits/${visit.id}/${kind}`, loc);
      await refresh();
      if (kind === "end") nav(`${base}/report/${visit.id}`);
      else toast.success("Visit started. Enjoy your time together.");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  const addNote = async () => {
    try {
      await api.post(`/families/${fid}/visits/${visit.id}/note`, { text: note });
      clearNote();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const live = visit.status === "in_progress";

  return (
    <div className="space-y-6">
      <PageTitle eyebrow="Visit mode" title={live ? "You're on a visit." : "Ready when you are."} sub={`${fmtDate(visit.scheduled_date)} · ${fmtTime(visit.start_time)} · ${visit.hours} hours planned`} />
      {brief && (
        <Link to={`${base}/briefs/${brief.id}`} data-testid="visit-open-brief" className="flex min-h-[64px] items-center gap-3 rounded-2xl bg-white px-5 text-lg font-semibold text-hf-green ring-1 ring-hf-line">
          <ClipboardList className="h-6 w-6 text-hf-coral" /> Read today's brief
        </Link>
      )}
      {!live ? (
        <button data-testid="visit-start-button" disabled={busy} onClick={() => act("start")} className="flex min-h-[160px] w-full flex-col items-center justify-center gap-3 rounded-3xl bg-hf-green text-hf-onprimary transition-[filter,transform] hover:brightness-110 active:scale-[0.98]">
          <Play className="h-12 w-12" />
          <span className="font-heading text-3xl font-semibold">{busy ? "Starting..." : "Start visit"}</span>
        </button>
      ) : (
        <>
          <div data-testid="visit-live-card" className="rounded-3xl bg-hf-green p-6 text-hf-cream">
            <p className="text-sm font-semibold uppercase tracking-widest text-hf-sun">Visit in progress</p>
            <p className="mt-2 font-heading text-5xl font-semibold"><Elapsed since={visit.started_at} /></p>
            <p className="mt-2 flex items-center gap-2 text-base text-hf-cream/80">
              <MapPin className="h-4 w-4" /> Started {fmtClock(visit.started_at)} · {visit.start_loc?.approx}
            </p>
          </div>
          <div className="hf-card">
            <label className="font-heading text-lg font-semibold">Quick note</label>
            <div className="mt-2 flex gap-2">
              <input data-testid="visit-note-input" className="hf-input" placeholder="Ate lunch at 12:30..." value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && note.trim() && addNote()} />
              <button data-testid="visit-note-add" aria-label="Add note" disabled={!note.trim()} onClick={addNote} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-hf-green text-hf-cream">
                <Plus className="h-6 w-6" />
              </button>
            </div>
            {visit.notes?.length > 0 && (
              <ul className="mt-4 space-y-2" data-testid="visit-notes-list">
                {visit.notes.map((n, i) => <li key={i} className="rounded-xl bg-hf-cream px-4 py-3 text-base"><span className="text-hf-muted">{fmtClock(n.at)}</span> · {n.text}</li>)}
              </ul>
            )}
          </div>
          <button data-testid="visit-not-right-button" onClick={() => setOpen(true)} className="hf-btn w-full border-2 border-hf-coral bg-hf-coralsoft text-hf-coraldark">
            <AlertTriangle className="h-5 w-5" /> Something's not right
          </button>
          <button data-testid="visit-end-button" disabled={busy} onClick={() => act("end")} className="hf-btn hf-btn-coral w-full">
            <Square className="h-5 w-5" /> {busy ? "Ending..." : "End visit"}
          </button>
        </>
      )}
      <p className="text-center text-base text-hf-muted">Start and end times, plus an approximate location, are saved as proof of the visit.</p>
      <NotRight open={open} setOpen={setOpen} briefId={brief?.id} />
      <Emergency911 />
    </div>
  );
}
